// useWebBleReady — estado VIVO de la impresora Bluetooth para la UI.
//
// El transporte (bleTransport) no emite eventos a React, así que hacemos polling de
// `isConnected(deviceId)` para que la UI refleje la conexión REAL (no solo "está
// emparejada en el perfil"). Cubre el requisito: el usuario debe saber EN TODO MOMENTO
// si la impresora está lista, dentro y fuera de Ajustes.

import { useEffect, useState } from 'react';
import { isConnected, isWebBleSupported } from '@utils/print/webble/bleTransport';
import { useWebBlePrintMode } from '@utils/print/webble/printMode';
import { useWebBlePrintersFromStore } from '@store/actions/webBlePrinters';

const POLL_MS = 2500;

// Tick compartido: re-renderiza a los consumidores cada POLL_MS sin que cada uno monte su
// propio intervalo. (Suficiente para un panel de ajustes / un badge.)
const useConnectionTick = (): number => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), POLL_MS);
    return () => clearInterval(id);
  }, []);
  return tick;
};

// ¿Está viva la conexión de un perfil concreto? (poll). Si no tiene deviceId → false.
export const useWebBlePrinterConnected = (deviceId: string | null | undefined): boolean => {
  useConnectionTick();
  return deviceId ? isConnected(deviceId) : false;
};

export type WebBleReadyStatus =
  | 'unsupported' // el navegador no soporta Web Bluetooth
  | 'off' // soportado pero el modo BT está apagado, o no hay impresoras emparejadas
  | 'ready' // modo BT on + TODAS las emparejadas+habilitadas están conectadas
  | 'disconnected'; // modo BT on + al menos una emparejada+habilitada SIN conexión

// pendingCount = emparejadas+habilitadas que aún no tienen conexión viva.
// Es lo que dispara el aviso "por confirmar" del badge global.

export interface WebBleReady {
  status: WebBleReadyStatus;
  pairedCount: number;
  connectedCount: number;
}

// Estado agregado para el badge global. Combina soporte + master toggle + conexión viva
// de todas las impresoras habilitadas y emparejadas.
export const useWebBleReady = (): WebBleReady => {
  useConnectionTick();
  const { enabled } = useWebBlePrintMode();
  const printers = useWebBlePrintersFromStore();

  if (!isWebBleSupported()) {
    return { status: 'unsupported', pairedCount: 0, connectedCount: 0 };
  }

  const paired = Object.values(printers).filter((p) => p.enabled && (p.deviceId || p.deviceName));
  const connectedCount = paired.filter((p) => p.deviceId && isConnected(p.deviceId)).length;

  if (!enabled || paired.length === 0) {
    return { status: 'off', pairedCount: paired.length, connectedCount };
  }
  // 'ready' SOLO cuando todas las emparejadas+habilitadas están conectadas.
  // Si falta al menos una (connectedCount < pairedCount) → 'disconnected', aunque otras
  // sí estén conectadas: ese caso (p.ej. Caja ok + Cocina caída) igual debe avisar.
  return {
    status: connectedCount === paired.length ? 'ready' : 'disconnected',
    pairedCount: paired.length,
    connectedCount,
  };
};
